import {useEffect, useMemo, useState} from "react";

import {
  dispatchCenterRequiresShift,
  isDispatchCenterResourceEnabled,
  isDispatchCenterRouteEnabled,
  isDispatchOperatorRole,
} from "@/src/modules/core/lib/dispatch-center-policy";
import {useGetHasPermissions, useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import type {Me} from "@/src/modules/core/types";

import {impersonationService} from "@/src/modules/support/services/impersonation";
import {useActiveShift} from "@/src/modules/officer/hooks";
import {shiftsService} from "@/src/modules/shifts/services";
import {companyOnGuardService} from "@/src/modules/company-on-guard/services";

import {MAIN_ROUTES, MANAGEMENT_ROUTES, SETTINGS_ROUTES, SUPPORT_ROUTES} from "../routes/authenticated";
import {routingService} from "../service";
import {Route} from "../types";

function sortRoutes(a: Route, b: Route) {
  return a.position && b.position ? a.position - b.position : 1;
}

function sortManagementRoutes(a: Route, b: Route) {
  return (a.label ?? "").localeCompare(b.label ?? "", undefined, {sensitivity: "base"});
}

function isDispatchCenterRouteAllowed(me: Me | null | undefined, route: Route): boolean {
  if (!isDispatchOperatorRole(me)) return true;
  if (route.key && !isDispatchCenterRouteEnabled(me, route.key)) return false;
  if (route.withPermissionGuard && !isDispatchCenterResourceEnabled(me, route.permission)) {
    return false;
  }
  return true;
}

export default function usePropertyRoutes() {
  const [mainRoutes, setMainRoutes] = useState<Route[]>([]);
  const [managementRoutes, setManagementRoutes] = useState<Route[]>([]);
  const mainStaticRoutes = useMainStaticRoutes();
  const mainDynamicRoutes = useMainDynamicRoutes();
  const managementStaticRoutes = useManagementStaticRoutes();
  const managementDynamicRoutes = useManagementDynamicRoutes();
  const settingsRoutes = useSettingsRoutes();
  const supportRoutes = useSupportRoutes();

  useEffect(() => {
    const mainRoutes = [...mainStaticRoutes, ...mainDynamicRoutes].sort(sortRoutes);

    const managementRoutes = [...managementStaticRoutes, ...managementDynamicRoutes].sort(
      sortManagementRoutes,
    );

    setMainRoutes(mainRoutes);
    setManagementRoutes(managementRoutes);
  }, [mainStaticRoutes, mainDynamicRoutes, managementStaticRoutes, managementDynamicRoutes]);

  return {
    mainRoutes,
    managementRoutes,
    settingsRoutes,
    supportRoutes,
  };
}

function useSupportRoutes() {
  const me = useStoredMe();

  return useMemo<Route[]>(() => {
    if (!impersonationService.getCanAccessSupportTools(me)) return [];

    return SUPPORT_ROUTES.filter((route) => route.key === "view-as-user").sort(sortRoutes);
  }, [me]);
}

function useMainStaticRoutes() {
  const getHasPermission = useGetHasPermissions();
  const hasHomeSection = routingService.getHasHomeSection();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();
  const {shift: activeShift, isPending: activeShiftPending} = useActiveShift();
  const mainStaticRoutes = useMemo<Route[]>(() => {
    if (userProperty?.service_tier === "free_inspection") {
      return [];
    }
    return MAIN_ROUTES.filter((route) => route.static && route.label).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      if (route.key === "dispatchdesk") {
        if (!isDispatchOperatorRole(me)) return false;
        if (dispatchCenterRequiresShift(me) && (activeShiftPending || !activeShift)) {
          return false;
        }
        return true;
      }
      if (route.key === "home") {
        if (!hasHomeSection) return false;
        if (isDispatchOperatorRole(me) && !dispatchCenterRequiresShift(me)) {
          return false;
        }
        return true;
      }
      if (route.withAdminGuard && !me?.isAdmin) {
        return false;
      }

      if (route.withPermissionGuard) {
        return getHasPermission(`${route.permission} show`);
      }

      return true;
    });
  }, [
    userProperty?.service_tier,
    hasHomeSection,
    getHasPermission,
    me?.isAdmin,
    me?.properties?.length,
    me,
    activeShift,
    activeShiftPending,
  ]);

  return mainStaticRoutes;
}

function useMainDynamicRoutes() {
  const getHasPermission = useGetHasPermissions();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();
  const mainDynamicRoutes = useMemo<Route[]>(() => {
    if (!userProperty) return [];
    if (userProperty.service_tier === "free_inspection") {
      return MAIN_ROUTES.filter(
        (route) =>
          route.key === "propertyinspectionreport" &&
          getHasPermission("propertyinspectionreport show"),
      );
    }
    return MAIN_ROUTES.filter((route) => !route.static).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      const section = userProperty.sections.find((section) => section.key === route.key);
      const isSectionGranted = !!section && section.granted;

      if (route.withPermissionGuard) {
        return isSectionGranted && getHasPermission(`${route.permission} show`);
      }

      return isSectionGranted;
    });
  }, [userProperty, getHasPermission, me]);

  return mainDynamicRoutes;
}

function useManagementStaticRoutes() {
  const getHasPermission = useGetHasPermissions();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();
  const managementStaticRoutes = useMemo<Route[]>(() => {
    const isFreeInspectionRestricted =
      userProperty?.service_tier === "free_inspection" && !me?.isAdmin && !me?.isSeniorSupervisor;
    if (isFreeInspectionRestricted) {
      return [];
    }
    return MANAGEMENT_ROUTES.filter((route) => route.static).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      if (route.key === "officer-on-guard") {
        return companyOnGuardService.getCanShowSection();
      }
      if (route.key === "reportrequests" && !me?.properties?.length) {
        return false;
      }
      if (route.withPermissionGuard) {
        return getHasPermission(`${route.permission} show`);
      }

      return true;
    });
  }, [userProperty?.service_tier, me?.isAdmin, me?.isSeniorSupervisor, me?.properties?.length, getHasPermission, me]);

  return managementStaticRoutes;
}

function useManagementDynamicRoutes() {
  const getHasPermission = useGetHasPermissions();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();
  const managementDynamicRoutes = useMemo<Route[]>(() => {
    if (!userProperty) return [];
    const isFreeInspectionRestricted =
      userProperty.service_tier === "free_inspection" && !me?.isAdmin && !me?.isSeniorSupervisor;
    if (isFreeInspectionRestricted) return [];
    return MANAGEMENT_ROUTES.filter((route) => !route.static).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      const section = userProperty.sections.find((section) => section.key === route.key);
      const isSectionGranted = !!section && section.granted;

      if (route.withPermissionGuard) {
        return isSectionGranted && getHasPermission(`${route.permission} show`);
      }

      return isSectionGranted;
    });
  }, [userProperty, me?.isAdmin, me?.isSeniorSupervisor, me]);

  return managementDynamicRoutes;
}

function useSettingsRoutes() {
  const getHasPermission = useGetHasPermissions();
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();

  return useMemo<Route[]>(() => {
    const isFreeInspectionRestricted =
      userProperty?.service_tier === "free_inspection" && !me?.isAdmin && !me?.isSeniorSupervisor;
    if (isFreeInspectionRestricted) return [];

    const staticRoutes = SETTINGS_ROUTES.filter((route) => route.static).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      if (route.key === "officer-on-guard") {
        return companyOnGuardService.getCanShowSection();
      }
      if (route.withAdminGuard && !me?.isAdmin) {
        return false;
      }
      if (route.key === "shifts" && !shiftsService.getCanAccessShiftsManagement(me)) {
        return false;
      }
      if (route.withPermissionGuard) {
        return getHasPermission(`${route.permission} show`);
      }
      return true;
    });

    if (!userProperty) {
      return staticRoutes.sort(sortRoutes);
    }

    const dynamicRoutes = SETTINGS_ROUTES.filter((route) => !route.static).filter((route) => {
      if (!isDispatchCenterRouteAllowed(me, route)) return false;
      if (route.key === "officer-on-guard") {
        return companyOnGuardService.getCanShowSection();
      }
      if (route.withAdminGuard && !me?.isAdmin) {
        return false;
      }
      if (route.key === "shifts" && !shiftsService.getCanAccessShiftsManagement(me)) {
        return false;
      }

      const section = userProperty.sections.find((section) => section.key === route.key);
      const isSectionGranted = !!section && section.granted;

      if (route.withPermissionGuard) {
        return isSectionGranted && getHasPermission(`${route.permission} show`);
      }

      return isSectionGranted;
    });

    return [...staticRoutes, ...dynamicRoutes].sort(sortRoutes);
  }, [getHasPermission, me?.isAdmin, me?.isResident, userProperty]);
}
